import React, { useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import type { Industry } from '../types';

interface IndustryDetailModalProps {
  industry: Industry | null;
  isOpen: boolean;
  onClose: () => void;
}

const IndustryDetailModal: React.FC<IndustryDetailModalProps> = ({ industry, isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !industry) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name={industry.icon} size={20} color="var(--color-primary)" />
            </div>
            <h2 className="text-2xl font-bold text-text-primary">{industry.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface rounded-lg transition-colors duration-fast"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6 lg:p-8">
          <div className="relative h-64 rounded-lg overflow-hidden mb-6">
            <Image
              src={industry.image}
              alt={industry.alt}
              className="w-full h-full object-cover"
            />
          </div>

          <p className="text-lg text-text-secondary mb-8">{industry.description}</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-surface rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Clock" size={20} className="text-success" />
                <span className="text-sm font-medium text-text-primary">Time Saved</span>
              </div>
              <div className="text-2xl font-bold text-success">{industry.metrics.timeSaved}</div>
            </div>
            <div className="bg-surface rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="TrendingDown" size={20} className="text-success" />
                <span className="text-sm font-medium text-text-primary">Cost Reduction</span>
              </div>
              <div className="text-2xl font-bold text-success">
                {industry.metrics.costReduction}
              </div>
            </div>
            <div className="bg-surface rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Zap" size={20} className="text-primary" />
                <span className="text-sm font-medium text-text-primary">Efficiency Gain</span>
              </div>
              <div className="text-2xl font-bold text-primary">{industry.metrics.efficiency}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold text-text-primary mb-4 flex items-center space-x-2">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <span>Common Challenges</span>
              </h3>
              <ul className="space-y-3">
                {industry.challenges.map((challenge, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="X" size={16} className="text-error mt-1 flex-shrink-0" />
                    <span className="text-sm text-text-secondary">{challenge}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-bold text-text-primary mb-4 flex items-center space-x-2">
                <Icon name="CheckCircle" size={20} className="text-success" />
                <span>Our Solutions</span>
              </h3>
              <ul className="space-y-3">
                {industry.solutions.map((solution, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <Icon name="Check" size={16} className="text-success mt-1 flex-shrink-0" />
                    <span className="text-sm text-text-secondary">{solution}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-bold text-text-primary mb-4 flex items-center space-x-2">
              <Icon name="Wrench" size={20} className="text-primary" />
              <span>Tools & Integrations</span>
            </h3>
            <div className="flex flex-wrap gap-2">
              {industry.tools.map((tool, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-surface text-text-primary text-sm rounded-lg border border-border"
                >
                  {tool}
                </span>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="default"
              fullWidth
              iconName="Calendar"
              iconPosition="left"
              onClick={onClose}
            >
              Book Free Consultation
            </Button>
            <Button
              variant="outline"
              fullWidth
              iconName="MessageCircle"
              iconPosition="left"
              onClick={onClose}
            >
              Chat on WhatsApp
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndustryDetailModal;